/**
 * Stage A — the one entry point for executing a technician timer act.
 *
 *   request (intent + the human facts the plan collected)
 *     → the server authority for that act (RPCs stay specialised)
 *     → ONE normalised result (outcomes.ts)
 *
 * and, separately, `runTimerSuccessEffects`: the surface's post-action effects
 * run ONLY for a confirmed success, in the plan's order. The wrappers this
 * calls already gate their own ticket-level effects (board cascade, Dayboard
 * broadcast, OCIS/Pensieve) on success — Stage 0A–0C.
 *
 * Presentation-agnostic: no DOM, no React, no toasts. A card, a phone surface
 * or a voice command can call it.
 */
import {
  startMyTimerWithScope,
  pauseTicketTimer,
  pauseMyTimerWithDiagnosticCapture,
  resumeTicketTimer,
  type DiagnosticCaptureAssertion,
} from '@/utils/ticketTimerManager';
import { finishMyWork } from '@/utils/ticketWorkLifecycle';
import {
  failedFromException,
  normalizeFinishResult,
  normalizePauseResult,
  normalizePauseWithCaptureResult,
  normalizeResumeResult,
  normalizeStartResult,
  type TimerActionResult,
  type TimerActionSuccess,
} from './outcomes';
import type { TimerSuccessEffect } from './actionPlan';
import { pauseOption, type PauseIntentFacts } from './pauseIntent';
import { destinationPayload, dispositionPayload, type FinishDestination, type JobDisposition } from './simplifiedStop';

/**
 * Where the work physically happens, when the act must move the vehicle there.
 * Today the only kind is a bay (the parked → bay pull-in). Kept as a tagged
 * shape so a later location model can add kinds without changing callers.
 */
export type TimerWorkTarget = { kind: 'bay'; bayId: string };

export type TimerActionRequest =
  | {
      intent: 'START';
      ticketId: string;
      /** The technician's explicit scope assertion. Never inferred. */
      jobIds: string[];
      /** Required when the vehicle must be brought in; null for an ordinary Start. */
      target: TimerWorkTarget | null;
      skipTimeUpdate?: boolean;
    }
  | {
      intent: 'PAUSE';
      ticketId: string;
      /** Stage D: the technician's ONE answer to "Why are you stopping work?". */
      pause: PauseIntentFacts;
      /** Optional findings for diagnostic jobs finished before this pause. */
      diagnosticCaptures: DiagnosticCaptureAssertion[];
    }
  | {
      intent: 'RESUME'; ticketId: string; techId?: string; shopId?: string;
      /** Oct 20 (D-4): the parked vehicle is brought into this bay in the same act (pull_in_and_resume_my_timer). */
      target?: TimerWorkTarget | null;
    }
  | {
      intent: 'STOP';
      ticketId: string;
      diagnosticCaptures: DiagnosticCaptureAssertion[];
      /** Stage E: where the FINAL worker leaves the vehicle (null: not needed / stays). */
      destination: FinishDestination | null;
      /** Final Stop: the technician's answer for each job no frozen scope points at. */
      jobDispositions?: Readonly<Record<string, JobDisposition>>;
      /** oct20a S1: what this session found, per diagnostic job (sessionConclusionPayload). */
      sessionConclusions?: ReadonlyArray<Record<string, unknown>>;
      /** OCT28 #14: the technician chose "Entire Repair Complete" (finish_entire_repair). */
      entireRepair?: boolean;
    }
  | {
      intent: 'END_WORK'; ticketId: string; destination: FinishDestination | null;
      jobDispositions?: Readonly<Record<string, JobDisposition>>;
      sessionConclusions?: ReadonlyArray<Record<string, unknown>>;
      entireRepair?: boolean;
    };

/** oct20a S1: the session conclusions travel only when there are some (the call shape is unchanged otherwise). */
const conclusionsArg = (c: ReadonlyArray<Record<string, unknown>> | undefined): [ReadonlyArray<Record<string, unknown>>] | [] =>
  c && c.length > 0 ? [c] : [];
/** OCT28 #14: the finish act — finish_entire_repair when chosen (the call shape is unchanged otherwise). */
const finish = (ticketId: string, destination: FinishDestination | null, captures: DiagnosticCaptureAssertion[],
  dispositions: Readonly<Record<string, JobDisposition>> | undefined,
  conclusions: ReadonlyArray<Record<string, unknown>> | undefined, entireRepair: boolean | undefined) =>
  entireRepair
    ? finishMyWork(ticketId, destinationPayload(destination), captures, dispositionPayload(dispositions ?? {}), conclusions ?? [],
        { entireRepair: true })
    : finishMyWork(ticketId, destinationPayload(destination), captures, dispositionPayload(dispositions ?? {}),
        ...conclusionsArg(conclusions));

async function invoke(req: TimerActionRequest): Promise<TimerActionResult> {
  switch (req.intent) {
    case 'START':
      return normalizeStartResult(await startMyTimerWithScope(req.ticketId, req.jobIds, {
        skipTimeUpdate: req.skipTimeUpdate,
        // The ONLY difference a parked Start makes: the atomic move-and-start authority.
        pullInToBayId: req.target?.kind === 'bay' ? req.target.bayId : null,
      }));
    case 'PAUSE': {
      // The server maps the answer to its category and legacy reason itself
      // (pause_my_work); the label here only words the OCIS event.
      const reason = pauseOption(req.pause.detail)?.pauseReason ?? 'Other';
      return req.diagnosticCaptures.length > 0
        ? normalizePauseWithCaptureResult(await pauseMyTimerWithDiagnosticCapture(req.ticketId, reason, req.diagnosticCaptures, req.pause))
        : normalizePauseResult(await pauseTicketTimer(req.ticketId, reason, req.pause));
    }
    case 'RESUME':
      return normalizeResumeResult(await resumeTicketTimer(req.ticketId, req.techId, req.shopId, {
        pullInToBayId: req.target?.kind === 'bay' ? req.target.bayId : null,
      }));
    // Both ends of work are ONE server act (finish_my_work). It ends this
    // technician's work through the unchanged Stop / End-Paused authorities (a
    // paused technician never gets a fabricated interval) and, only as the
    // last live BOH work, completes the ticket, leaves the vehicle on site and
    // hands the ticket to the FOH queue in the same transaction.
    case 'STOP':
      return normalizeFinishResult('STOP', await finish(
        req.ticketId, req.destination, req.diagnosticCaptures, req.jobDispositions, req.sessionConclusions, req.entireRepair));
    case 'END_WORK':
      return normalizeFinishResult('END_WORK', await finish(
        req.ticketId, req.destination, [], req.jobDispositions, req.sessionConclusions, req.entireRepair));
  }
}

/** Execute one act. Never throws; an exception is a FAILED result. */
export async function executeTimerAction(req: TimerActionRequest): Promise<TimerActionResult> {
  try {
    return await invoke(req);
  } catch (error) {
    console.error(`[executeTimerAction] ${req.intent} failed:`, error);
    return failedFromException(req.intent, error);
  }
}

/** The surface's implementations of each post-success effect. */
export type TimerSuccessEffectHandlers = Partial<
  Record<TimerSuccessEffect, (result: TimerActionSuccess) => void | Promise<void>>
>;

/**
 * Run the plan's success effects, in order — for a confirmed success ONLY.
 * Anything else (requires_action, refused, failed, unknown) runs nothing and
 * returns an empty list. One effect failing never undoes the committed act or
 * skips the rest: the timer success is authoritative.
 */
export async function runTimerSuccessEffects(
  result: TimerActionResult,
  effects: readonly TimerSuccessEffect[],
  handlers: TimerSuccessEffectHandlers,
): Promise<TimerSuccessEffect[]> {
  if (result.kind !== 'success') return [];
  const ran: TimerSuccessEffect[] = [];
  for (const effect of effects) {
    const handler = handlers[effect];
    if (!handler) continue;
    try {
      await handler(result);
      ran.push(effect);
    } catch (error) {
      console.warn(`[runTimerSuccessEffects] ${effect} failed after a committed ${result.intent}`, error);
    }
  }
  return ran;
}
